import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, setAuthToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "@/components/app/BrandMark";

function TrialBullets() {
  const items = [
    "Manager dashboard with live HSE points",
    "QR codes for every tool",
    "Employee mobile app — scan & record",
    "PDF and CSV exposure reports",
    "Hand-health check-ins",
    "Up to 10 employees, 1 manager",
  ];
  return (
    <ul className="space-y-2 text-sm text-slate-300">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-2">
          <Check className="h-4 w-4 mt-0.5 text-emerald-400" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function TrialPanel() {
  return (
    <div
      className="hidden lg:flex relative bg-slate-900"
      style={{
        backgroundImage:
          "url(https://images.unsplash.com/photo-1758876734777-dcc6981f3671?w=1400&q=80)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-slate-900/85" />
      <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
        <BrandMark size="lg" tone="light" />
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400 mb-2">
            7-day free trial
          </p>
          <h1 className="font-display text-4xl xl:text-5xl font-black tracking-tighter mb-4">
            Bring HAVS exposure
            <br />
            under control today.
          </h1>
          <p className="text-slate-300 text-base max-w-md leading-relaxed mb-8">
            Set up your site in under a minute. No credit card to start. Cancel
            anytime by deleting your account from inside the app.
          </p>
          <TrialBullets />
          <div className="mt-10 flex items-center gap-2 text-sm text-slate-300">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            UK HSE compliant · GDPR account deletion built in
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Signup() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name: "",
    manager_name: "",
    email: "",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const [device, setDevice] = useState(null);

  // If they're already signed in AND there's no device card to show, send them home.
  if (user && !device) {
    return <Navigate to={user.role === "manager" ? "/dashboard" : "/worker"} replace />;
  }

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", form);
      if (data?.token) setAuthToken(data.token);
      // Show device credentials FIRST. We refresh auth state only when the user
      // continues — otherwise the route guard would redirect away too fast.
      setDevice(data.device_login);
      toast.success("Trial started — welcome to HAVS Guard NT!");
    } catch (err) {
      console.error("Signup failed:", err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign-up failed");
    } finally {
      setBusy(false);
    }
  };

  const continueToDashboard = async () => {
    await refresh();
    navigate("/dashboard", { replace: true });
  };

  if (device) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <Card className="max-w-md w-full p-8" data-testid="signup-device-card">
          <BrandMark size="md" />
          <h1 className="font-display text-2xl font-bold mt-6 mb-2">
            Save your employee device login
          </h1>
          <p className="text-sm text-slate-600 mb-5">
            Hand these credentials to anyone using the employee mobile app on
            site. You can change them later. We'll show this once.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2 mb-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Email</p>
              <p className="font-mono text-sm break-all" data-testid="device-email">
                {device.email}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Password</p>
              <p className="font-mono text-sm" data-testid="device-password">
                {device.password}
              </p>
            </div>
          </div>
          <Button
            onClick={continueToDashboard}
            className="w-full h-11 bg-slate-900 hover:bg-slate-800"
            data-testid="signup-continue-btn"
          >
            I've saved them — continue
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <TrialPanel />
      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md p-8" data-testid="signup-card">
          <div className="flex items-center gap-2 mb-2 lg:hidden">
            <BrandMark size="md" tone="dark" />
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-2">
            Start your 7-day free trial
          </p>
          <h2 className="font-display text-3xl font-bold text-slate-900 mb-1">
            Create your site
          </h2>
          <p className="text-sm text-slate-600 mb-8">
            One company, one manager, up to 10 employees.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Company / site name</Label>
              <Input
                value={form.company_name}
                onChange={update("company_name")}
                className="h-11 mt-2"
                placeholder="Acme Construction Ltd"
                required
                data-testid="signup-company-input"
              />
            </div>
            <div>
              <Label>Your name</Label>
              <Input
                value={form.manager_name}
                onChange={update("manager_name")}
                className="h-11 mt-2"
                required
                data-testid="signup-name-input"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={update("email")}
                className="h-11 mt-2"
                required
                data-testid="signup-email-input"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={form.password}
                onChange={update("password")}
                className="h-11 mt-2"
                minLength={8}
                required
                data-testid="signup-password-input"
              />
              <p className="text-xs text-slate-500 mt-1">8+ characters.</p>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800"
              data-testid="signup-submit-btn"
            >
              {busy ? "Creating site…" : "Start free trial"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-600 text-center">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-medium text-slate-900 underline"
              data-testid="signup-login-link"
            >
              Sign in
            </Link>
          </p>
          <p className="mt-4 text-xs text-slate-400 text-center">
            By creating an account you agree this app stores worksite data
            necessary for HSE compliance. You can delete your account and all
            data at any time from Settings.
          </p>
        </Card>
      </div>
    </div>
  );
}
